/**
 * Custom DatePicker Component
 * Extends Ant Design DatePicker with custom styling
 */

import { DatePicker as AntDatePicker, DatePickerProps } from 'antd';
import classNames from 'classnames';
import './CustomDatePicker.scss';

export interface CustomDatePickerProps extends Omit<DatePickerProps, 'variant'> {
  /** Variant */
  pickerVariant?: 'default' | 'filled' | 'bordered';
  /** Error state */
  error?: boolean;
}

/**
 * CustomDatePicker Component
 * Standardized date picker with MM/DD/YYYY format and Today button disabled
 */
export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  pickerVariant = 'default',
  error = false,
  className,
  format,
  showToday = false, // Always false - Today button is disabled globally
  picker,
  ...rest
}) => {
  const pickerClasses = classNames(
    'custom-datepicker',
    `custom-datepicker--${pickerVariant}`,
    {
      'custom-datepicker--error': error,
    },
    className
  );

  // Set default format based on picker type
  // MM/YYYY for month pickers, MM/DD/YYYY for date pickers
  const defaultFormat = format || (picker === 'month' ? 'MM/YYYY' : 'MM/DD/YYYY');

  // Always disable Today button - override any passed prop
  return (
    <AntDatePicker
      {...rest}
      className={pickerClasses}
      format={defaultFormat}
      showToday={false} // Always false - enforced globally
      picker={picker}
    />
  );
};

/**
 * CustomRangePicker Component
 * Standardized range picker with MM/DD/YYYY format and Today button disabled
 */
export const CustomRangePicker: React.FC<any> = ({ 
  className, 
  format,
  showToday = false, // Always false - Today button is disabled globally
  ...rest 
}) => {
  const rangeClasses = classNames('custom-rangepicker', className);
  const defaultFormat = format || 'MM/DD/YYYY';
  
  // Always disable Today button - override any passed prop
  return (
    <AntDatePicker.RangePicker 
      {...rest} 
      className={rangeClasses}
      format={defaultFormat}
      showToday={false} // Always false - enforced globally
    />
  );
};

export default CustomDatePicker;

